/* tslint:disable:semicolon*/
import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {PlatformQueryService} from '@core/services/platform-query.service';
import {DriveTreeItem, FileStoreFlatNode, TeamDrivesTree} from '@features/file-manager/models/file-manager.model';
import {PlatformQuery} from '@shared/models/platform-query/platform-query';
import {take, takeUntil} from 'rxjs/operators';
import {FlatTreeControl} from '@angular/cdk/tree';
import {MatTreeFlatDataSource, MatTreeFlattener} from '@angular/material/tree';
import {MagicLinkModel} from '@shared/models/platform-tool-models';
import {FileStoreCommonService} from '@features/file-manager/services/file-store-common.service';
import {select, Store} from '@ngrx/store';
import {getBreadCrumbs, ParticleState} from '@shared/store/reducers/particle.reduer';
import {BreadcrumbModel} from '@shared/models/breadcrumb.model';
import {InvalidateSubject} from '@shared/helpers/invalidate.subject';
import {Subject} from 'rxjs';
import {PlatformSidebarState} from '@shared/components/platform-sidebar/store/platform-sidebar.reducer';

/**
 * @title Tree with flat nodes
 */
@Component({
    standalone: false,
    selector: 'app-file-store-tree-view',
  templateUrl: './file-store-tree-view.component.html',
  styleUrls: ['./file-store-tree-view.component.scss']
})
export class FileStoreTreeViewComponent implements OnInit, OnDestroy {
  @Input() magicLinkModel: MagicLinkModel;
  @Input() isClientDrive: boolean;
  @Input() driveId: string;
  @Input() invalidateSubject = new InvalidateSubject();
  public isReady: boolean;
  public tree: TeamDrivesTree;
  nestedNodeMap = new Map<DriveTreeItem, FileStoreFlatNode>();
  treeControl: FlatTreeControl<FileStoreFlatNode>;
  treeFlattener: MatTreeFlattener<DriveTreeItem, FileStoreFlatNode>;
  dataSource: MatTreeFlatDataSource<DriveTreeItem, FileStoreFlatNode>;
  breadcrumbs: BreadcrumbModel[];
  highlightedNode: FileStoreFlatNode;
  ngUnsubscribe$ = new Subject<void>();

  constructor(private platformQueryService: PlatformQueryService,
              private fileStoreCommonService: FileStoreCommonService,
              private particleStore: Store<ParticleState>,
              private platformSidebarStore: Store<PlatformSidebarState>) {

  }

  ngOnInit(): void {
    this.getTreeData();
    this.invalidateSubject.getObservale().pipe(takeUntil(this.ngUnsubscribe$)).subscribe(() =>
      this.getTreeData()
    );
  }
  private getTreeData() {
    const query = this.getTreeQuery();
    if (query.api && query.name) {
      this.platformQueryService.executeQuery(query).pipe(take(1)).subscribe(response => {
        console.log('+++++++++++++++++++++++++++++++++++', response.fieldValues['TeamDrivesTree']);
        this.tree = response.fieldValues['TeamDrivesTree'];
        this.initTreeView();
        this.dataSource.data = response.fieldValues['TeamDrivesTree'].items;
        this.isReady = !!this.tree;
        this.expandTreeNode();
      });
    }
  }
  private getTreeQuery() {
    if (this.magicLinkModel.fieldValues && this.magicLinkModel.fieldValues['LeftPanelItemQuery']) {
      return this.magicLinkModel.fieldValues['LeftPanelItemQuery'];
    }
    const query = new PlatformQuery();
    query.name = 'TeamDrivesTreeQuery';
    query.api = 'FileStoreApi';
    query.isCamelCase = true;
    query.fieldValues['IsClientDrive'] = this.isClientDrive;
    query.fieldValues['DriveId'] = this.driveId;

    return query;
  }

  hasChild = (_: number, node: FileStoreFlatNode): boolean => node.expandable;

  onTreeNodeClicked($event: MouseEvent, node: FileStoreFlatNode) {
    $event.stopPropagation();
    if (this.magicLinkModel.additionalCommands['Open']) {
      delete this.magicLinkModel.additionalCommands['Open'].fieldValues?.PlatformLinkId;
    }
    this.fileStoreCommonService.executeFormAdditionalCommand({
      itemId: node.itemId,
      parentId: node.parentId,
      driveId: null
    }, node.type, this.magicLinkModel);
  }

  private transformer = (node: DriveTreeItem, level: number): FileStoreFlatNode => {
    const existingNode = this.nestedNodeMap.get(node);
    const flatNode = existingNode && existingNode.name === node.name
      ? existingNode
      : new FileStoreFlatNode();

    flatNode.level = level;
    flatNode.expandable = true;
    flatNode.name = node.name;
    flatNode.level = level;
    flatNode.type = node.type;
    flatNode.itemId = node.itemId;
    flatNode.parentId = node.parentId;
    flatNode.featureId = node.featureId;
    flatNode.childrenLength = node.children.length;
    flatNode.icon = node.icon;

    this.nestedNodeMap.set(node, flatNode);
    return flatNode;
  };

  private initTreeView() {
    this.particleStore.pipe(select(getBreadCrumbs)).subscribe(res => this.breadcrumbs = res);
    this.treeFlattener = new MatTreeFlattener(
      this.transformer, this.getLevel, this.isExpandable, this.getChildren);
    this.treeControl = new FlatTreeControl<FileStoreFlatNode>(
      this.getLevel, this.isExpandable);
    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
  }

  private getLevel = (node: FileStoreFlatNode) => node && node.level;

  private isExpandable = (node: FileStoreFlatNode) => node && node.expandable;

  private getChildren = (node: DriveTreeItem): DriveTreeItem[] => node && node.children;

  private getParentNode(node: FileStoreFlatNode): FileStoreFlatNode | null {
    const currentLevel = this.getLevel(node);

    if (currentLevel < 1) {
      return null;
    }

    const startIndex = this.treeControl.dataNodes.indexOf(node) - 1;

    for (let i = startIndex; i >= 0; i--) {
      const currentNode = this.treeControl.dataNodes[i];

      if (this.getLevel(currentNode) < currentLevel) {
        return currentNode;
      }
    }
    return null;
  }

  private expandTreeNode() {
    let isLeafNode = false;
    let breadcrumbsToExpand = this.breadcrumbs.slice();

    if(this.magicLinkModel.fieldValues['IsFlowExecution']){
      if(this.magicLinkModel.fieldValues['OriginalBreadcrumbs']){
        breadcrumbsToExpand = this.magicLinkModel.fieldValues['OriginalBreadcrumbs'];
      }
    } 

    if (breadcrumbsToExpand.length <= 1) {
      this.treeControl.expand(this.treeControl.dataNodes[0]);
    }
    breadcrumbsToExpand.slice().reverse().forEach((item: BreadcrumbModel) => {
      this.treeControl.dataNodes.forEach((dataNode: FileStoreFlatNode) => {
        if (dataNode.itemId === item.id) {
          this.treeControl.expand(dataNode);
          if (!isLeafNode) {
            this.highlightedNode = dataNode;
            isLeafNode = true;
          }
          return;
        }
      });
      if (isLeafNode) {
        return;
      }
    });
  };

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
